export interface User {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'master_admin' | 'sales' | 'accounts' | 'customer_support' | 'staff' | 'technician' | 'owner' | 'pending' | 'state_manager' | 'site_manager' | 'team_lead' | 'site_engineer' | 'storekeeper' | 'field_staff';
  state?: string;
  // Multi-Tenancy & ABAC
  organizationId?: string;
  certifications?: string[];
  // Verisure Additions
  verisureRole?: 'admin' | 'technician';
  assignedSiteIds?: string[];
  signature?: string; // Kept from existing feature
  idNumber?: string;
  idCardUrl?: string; // Added for KYC ID upload
  // Freemium & Subscriptions
  subscriptionTier?: 'free' | 'premium';
  subscriptionStatus?: 'active' | 'defaulted' | 'suspended';
  subscriptionExpiry?: any;
  accountCategory?: 'individual' | 'company' | 'staff';
}

export const FREEMIUM_LIMITS = {
  INDIVIDUAL: 5,
  ORGANIZATION: 10,
} as const;

export interface Organization {
  id: string; // The tenant organizationId
  companyName: string;
  adminUserId: string;
  status: 'pending' | 'active' | 'rejected';
  documents: {
    cacUrl?: string;
    memartUrl?: string;
    applicationUrl?: string;
  };
  subscriptionTier?: 'free' | 'premium';
  subscriptionStatus?: 'active' | 'defaulted' | 'suspended';
  subscriptionExpiry?: any;
  createdAt: any;
}

export interface SystemSettings {
  id: string;
  bankDetails?: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    instructions: string;
  };
  updatedAt: any;
}

export interface Notification {
  id?: string;
  organizationId: string;
  title: string;
  message: string;
  isRead: boolean;
  type: 'info' | 'warning' | 'payment_default' | 'success';
  createdAt: any;
}

export interface Site {
  _id?: string;
  id?: string; // Firebase id
  siteId: string;
  siteName: string;
  address: string;
  latitude: number;
  longitude: number;
  region: string;
  state: string;
  status: 'active' | 'inactive' | 'maintenance';
}

export interface Report {
  _id?: string;
  id?: string;
  reportType: 'DAR' | 'weekly' | 'monthly';
  fieldStaffId: string;
  siteId: string;
  description: string;
  photos: string[];
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface Asset {
  _id?: string;
  id?: string;
  assetId: string;
  assetName: string;
  assetType: 'consumable' | 'equipment' | 'vehicle';
  quantity: number;
  currentHolder?: string;
  status: 'available' | 'in_use' | 'maintenance' | 'retired';
}

export interface Ticket {
  _id?: string;
  id?: string;
  ticketId: string;
  title: string;
  description: string;
  siteId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  createdAt: string;
}

// Deprecated Old Verisure Models (kept temporarily for compilation during migration)
export interface VerisureSite { id?: string; name: string; address: string; createdBy: string; status: 'active' | 'archived'; createdAt: any; }
export interface VerisureCabinet { id?: string; siteId: string; cabinetLabel: string; qrCodeUrl: string; status: 'active' | 'inactive'; createdAt: any; }
export interface VerisureLog { id?: string; cabinetId: string; siteId: string; technicianId: string; technicianName: string; notes: string; statusTag: string; photoUrls: string[]; timestamp: any; geolocation?: { latitude: number; longitude: number; }; }

// --- New Universal VERISURE Models ---

export type EquipmentSize = 'compact' | 'standard' | 'large' | 'industrial';

export type ItemCategory =
  | 'home_appliance'
  | 'consumer_electronics'
  | 'vehicle'
  | 'industrial'
  | 'electrical'
  | 'mechanical'
  | 'telecom'
  | 'aerospace'
  | 'medical'
  | 'other';

export interface VerisureItem {
  id?: string;
  assetId: string;
  category: ItemCategory;
  type?: string;
  make: string;
  model: string;
  year?: number;
  serialNumber?: string;
  ownerName: string;
  ownerUserId?: string;
  organizationId?: string;
  qrCodeUrl: string;
  conditionScore: number;
  equipmentSize?: EquipmentSize;
  estimatedOriginalPrice?: number;
  priceCurrency?: string;
  registeredAt?: any;
  createdAt?: any;
  outletOrigin?: string;
  ownerEmail?: string;
  ownerPhone?: string;
}

export interface VerisureRepair {
  id?: string;
  itemId: string;
  technicianId: string;
  technicianName: string;
  date: any;
  report: string;
  partsChanged: string;
  faultyPartPhotos: string[];
  replacementPartPhotos: string[];
  cost?: number;
  costCurrency: string;
  gpsLat?: number;
  gpsLng?: number;
  conditionScore: number;
  immutable?: boolean;
  partsAuthenticity?: 'oem_certified' | 'aftermarket_approved' | 'generic';
  organizationId?: string;
  createdAt?: any;
}

export interface VerisureClaim {
  id?: string;
  repairId: string;
  itemId: string;
  ownerName: string;
  ownerUserId?: string;
  description: string;
  amount: number;
  currency: string;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  submittedAt: any;
  resolvedAt?: any;
  referenceCode: string;
  organizationId?: string;
}

export interface TechnicianReview {
  id: string;
  ownerName: string;
  ownerAvatar?: string;
  rating: number; // 1 to 5
  comment: string;
  date: string;
  itemTitle?: string;
  verifiedRepairId?: string;
}

export interface MaintainedEquipmentSummary {
  itemId: string;
  assetId: string;
  make: string;
  model: string;
  category: ItemCategory;
  repairsCount: number;
  conditionScore: number;
  lastServicedDate: string;
}

export interface VerisureTechnician {
  id?: string;
  userId: string;
  name: string;
  avatarUrl?: string;
  bio?: string;
  phone?: string;
  email?: string;
  location?: string;
  state?: string;
  certificationLevel: string; // e.g. "Master Certified Specialist (Level 4)"
  specializations: string[];
  claimsAuditComplianceRating?: string; // e.g. "Insurer-Approved Gold Tier"
  verifiedClaimsSupported?: number; // e.g. 18
  jobsCompleted: number;
  conditionScoreAvg: number;
  rating: number; // e.g. 4.9
  reviewCount: number;
  responseTime?: string; // e.g. "< 2 Hours"
  isAvailable?: boolean;
  reviews?: TechnicianReview[];
  maintainedEquipments?: MaintainedEquipmentSummary[];
  createdAt: any;
}

export interface ResaleValuation {
  originalMSRP: number;
  currency: string;
  currentEstimatedValue: number;
  verisureValueBonus: number; // Extra value retained due to verified maintenance history
  valueRetentionPercentage: number;
  depreciationRate: number;
  repairCyclesCount: number;
  conditionScoreImpact: number;
  partsAuthenticityBonus: number;
  confidenceGrade: 'A+' | 'A' | 'B' | 'C' | 'D';
  marketComparisonUnverified: number;
  summaryRecommendation: string;
}

export interface VerisureBlankQr {
  id?: string;
  tagId: string; // e.g. VRS-TAG-89210
  batchId?: string; // e.g. BATCH-2026-IKEJA-01
  outletName: string; // e.g. "Ikeja Computer Village Store"
  outletLocation?: string; // Physical location / state
  status: 'unclaimed' | 'registered';
  qrPayloadUrl: string;
  notes?: string;
  createdBy: string;
  createdByName?: string;
  createdByRole?: 'master_admin' | 'super_admin';
  createdAt: any;
  // Claiming / Tagging Details
  claimedAt?: any;
  claimedByName?: string;
  claimedByEmail?: string;
  claimedByPhone?: string;
  itemId?: string; // ID of the created VerisureItem
  deviceSummary?: {
    make: string;
    model: string;
    category: string;
    serialNumber?: string;
  };
}
